import React, { useMemo, useState } from 'react';
import { PencilLineIcon, SearchIcon, UserSearchIcon } from 'lucide-react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { ConsultationTypeBadge } from '../../components/ui/ConsultationTypeBadge';
import { AmendFileEditor } from './AmendFileEditor';
const MAX_RESULTS = 30;
const digitsOnly = (value: string) => value.replace(/\D/g, '');

/**
 * Amend File: Search patient → select existing visit → amend. Opens the
 * existing encounter in place; never creates a patient or encounter.
 */
export function AmendFile() {
  const {
    patients,
    encounters
  } = useData();
  const [query, setQuery] = useState('');
  const [patientId, setPatientId] = useState<string | null>(null);
  const [encounterId, setEncounterId] = useState<string | null>(null);
  const patient = patients.find(p => p.id === patientId);
  const encounter = encounters.find(e => e.id === encounterId);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const qDigits = digitsOnly(q);
    return patients.filter(p => p.bmcFileNumber.toLowerCase().includes(q) || p.id.toLowerCase() === q || `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) || `${p.lastName} ${p.firstName}`.toLowerCase().includes(q) || qDigits.length >= 3 && digitsOnly(p.contact || '').includes(qDigits)).sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
  }, [patients, query]);
  const visits = useMemo(() => patientId ? encounters.filter(e => e.patientId === patientId).sort((a, b) => b.startedAt.localeCompare(a.startedAt)) : [], [encounters, patientId]);
  if (patient && encounter?.clinicalNote) {
    return <AmendFileEditor key={encounter.id} patient={patient} encounter={encounter} onBack={() => setEncounterId(null)} data-id="element-5705" data-mp="xt068enueo2m" />;
  }
  return <section aria-labelledby="amend-file-heading" className="space-y-5" data-id="element-5706" data-mp="rc8500qa80z0">
      <div className="rounded-xl bg-white p-4 shadow-sm sm:p-6" data-id="element-5707" data-mp="reb4rch8l8gx">
        <h2 id="amend-file-heading" className="text-lg font-bold text-gray-900" data-id="element-5708" data-mp="me6p1oo5khrr">
          Amend File
        </h2>
        <p className="text-sm text-gray-500" data-id="element-5709" data-mp="hflm3hhinjzm">
          Find an existing patient file to correct or add notes to, whichever
          clinician completed it.
        </p>
        <div className="relative mt-4" data-id="element-5710" data-mp="1a2g3u6my859e">
          <label htmlFor="amend-search" className="sr-only" data-id="element-5711" data-mp="1a3ho91puu43s">
            Search by patient name, file number or phone number
          </label>
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" data-id="element-5712" data-mp="10dg2igkz4xs1" />
          <input id="amend-search" type="search" autoFocus autoComplete="off" value={query} onChange={e => {
          setQuery(e.target.value);
          setPatientId(null);
        }} placeholder="Patient name, file number (BMC…) or phone number" className="block w-full rounded-lg border border-gray-300 py-3 pl-10 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-5713" data-mp="1xtv9a8a0v2ov" />
        </div>

        <div className="mt-4" aria-live="polite" data-id="element-5714" data-mp="2k92ax8pbzlkv">
          {!query.trim() ? <div className="py-10 text-center text-gray-500" data-id="element-5715" data-mp="2k6zlaps3bugo">
              <UserSearchIcon className="mx-auto mb-2 h-10 w-10 text-gray-300" aria-hidden="true" data-id="element-5716" data-mp="2o9ma0htgsz9n" />
              <p className="text-sm" data-id="element-5717" data-mp="2t2ff56sui9m9">Search for the patient whose file you need to amend.</p>
            </div> : results.length === 0 ? <p className="py-10 text-center text-sm text-gray-500" data-id="element-5718" data-mp="2y2bjx89da52v">
              No patient files match "{query.trim()}".
            </p> : <div className="overflow-x-auto rounded-lg border border-gray-200" data-id="element-5719" data-mp="37zg79bey86l9">
              <table className="min-w-full divide-y divide-gray-200 text-sm" data-id="element-5720" data-mp="8jvu5houmb6x">
                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-600" data-id="element-5721" data-mp="3lu9isbry3tc">
                  <tr data-id="element-5722" data-mp="iaqicrlnnkv0">
                    <th scope="col" className="px-4 py-3" data-id="element-5723" data-mp="i9xj0bp0zxmw">File No.</th>
                    <th scope="col" className="px-4 py-3" data-id="element-5724" data-mp="db436cm9nujk">Patient</th>
                    <th scope="col" className="px-4 py-3" data-id="element-5725" data-mp="3lr60gufucvh3">Sex / Age</th>
                    <th scope="col" className="px-4 py-3" data-id="element-5726" data-mp="3lrfq2gwz50m9">Contact</th>
                    <th scope="col" className="px-4 py-3" data-id="element-5727" data-mp="3gt4dhyfaoc0c">Visits</th>
                    <th scope="col" className="px-4 py-3" data-id="element-5728" data-mp="3urlwxjy632cg"><span className="sr-only" data-id="element-5729" data-mp="3usnb3xahc4ye">Select</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white" data-id="element-5730" data-mp="3qopebt8mn68i">
                  {results.slice(0, MAX_RESULTS).map(p => {
                const selected = p.id === patientId;
                const count = encounters.filter(e => e.patientId === p.id).length;
                return <tr key={p.id} className={selected ? 'bg-blue-50' : undefined} data-id="element-5731" data-mp="1di98h8ciph4c">
                        <td className="whitespace-nowrap px-4 py-3 font-mono text-blue-700" data-id="element-5732" data-mp="1ik8b2g0foa08">{p.bmcFileNumber}</td>
                        <td className="whitespace-nowrap px-4 py-3 font-medium text-gray-900" data-id="element-5733" data-mp="13nhaw4ixqu9k">
                          {p.firstName} {p.lastName}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-gray-700" data-id="element-5734" data-mp="1wbd7zacf2o6v">
                          {p.sex} · {p.age} yrs
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-gray-700" data-id="element-5735" data-mp="1mkikk2nfpiju">{p.contact || '—'}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-gray-700" data-id="element-5736" data-mp="1ritpq02sb4vq">{count}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right" data-id="element-5737" data-mp="2f8ebsru8ii98">
                          <Button type="button" size="sm" variant={selected ? 'primary' : 'outline'} onClick={() => setPatientId(p.id)} aria-pressed={selected} data-id="element-5738" data-mp="2jzmktmrnk1lv">
                            {selected ? 'Selected' : 'Select'}
                          </Button>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
              {results.length > MAX_RESULTS && <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-500" data-id="element-5739" data-mp="266aiv772p5pr">
                  Showing first {MAX_RESULTS} of {results.length} — refine your search.
                </p>}
            </div>}
        </div>
      </div>

      {patient && <section aria-labelledby="amend-visits-heading" className="rounded-xl bg-white p-4 shadow-sm sm:p-6" data-id="element-5740" data-mp="1s69j5v2jo4bv">
          <h3 id="amend-visits-heading" className="font-semibold text-gray-900" data-id="element-5741" data-mp="1n7g3bvzsc18j">
            Visits for {patient.firstName} {patient.lastName}
          </h3>
          <p className="text-sm text-gray-500" data-id="element-5742" data-mp="2ovygs0a2q6y">Choose the visit to amend.</p>
          {visits.length === 0 ? <p className="py-8 text-center text-sm text-gray-500" data-id="element-5743" data-mp="7q1q0mi7c7ew">This patient has no recorded visits.</p> : <ul className="mt-3 divide-y divide-gray-100 rounded-lg border border-gray-200" data-id="element-5744" data-mp="gon4a5gn509v">
              {visits.map(visit => {
          const note = visit.clinicalNote;
          const amendments = note?.editHistory?.length ?? 0;
          return <li key={visit.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-id="element-5745" data-mp="3l0qzc3plgaa4">
                    <div className="min-w-0" data-id="element-5746" data-mp="3tvf65yati011">
                      <div className="flex flex-wrap items-center gap-2" data-id="element-5747" data-mp="3tw8g1852p5ne">
                        <p className="font-medium text-gray-900" data-id="element-5748" data-mp="2xe2b709zrll2">
                          {new Date(visit.startedAt).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })}
                        </p>
                        <ConsultationTypeBadge type={visit.consultationType} size="sm" data-id="element-5749" data-mp="1pua2k1wfv5v5" />
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600" data-id="element-5750" data-mp="24qrd4qwt0ggn">
                          {visit.stage === 'COMPLETED' ? 'Completed' : `In progress · ${visit.stage}`}
                        </span>
                        {amendments > 0 && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800" data-id="element-5751" data-mp="28qjmv6xxosnl">
                            Amended {amendments}×
                          </span>}
                      </div>
                      <p className="mt-0.5 truncate text-sm text-gray-600" data-id="element-5752" data-mp="2ijqpwl57bwki">
                        {note ? `${note.clinicianName || 'Clinician not recorded'} · ${note.workingDiagnosis?.join(', ') || 'No diagnosis recorded'}` : 'No clinical notes recorded for this visit'}
                      </p>
                    </div>
                    <Button type="button" size="sm" onClick={() => setEncounterId(visit.id)} disabled={!note} title={note ? undefined : 'Nothing to amend — no clinical notes on this visit'} data-id="element-5753" data-mp="3g25zyylgcege">
                      <PencilLineIcon className="mr-1.5 h-4 w-4" aria-hidden="true" data-id="element-5754" data-mp="2sbjw7jihdgc7" />
                      Amend
                    </Button>
                  </li>;
        })}
            </ul>}
        </section>}
    </section>;
}